import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, resolveDirective as _resolveDirective, openBlock as _openBlock, createBlock as _createBlock, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vShow as _vShow, mergeProps as _mergeProps, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "d-flex justify-space-between align-center" }
const _hoisted_2 = { class: "text-h6" }
const _hoisted_3 = {
  key: 0,
  style: {"display":"contents"}
}
const _hoisted_4 = {
  key: 1,
  style: {"display":"contents"}
}

import { ref, computed } from 'vue'
import {
  Citizen,
  CitizenSex,
  citizenSexName,
  DocumentType,
  documentTypeName,
  citizenCategoryName,
  MunicipalEntitiesService,
  CitizenCategory,
  enumGetValues,
  calcCitizenCategory,
  AppAuthService,
  Permissions,
  AddressSuggesterService,
  AddressSuggestion,
  AddressLevel,
} from '@prodcard/core'
import { CitizenDocumentOpts, CitizenOpts } from '../interfaces'
import { useWatcherSavedRef } from '../../../../helpers'
import { useVuelidate, ErrorObject } from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'
import {
  VPhoneField,
  VDateFieldV2,
  VMunicipalEntitiesSelect,
  DeleteConfirmationDialog,
  AddressSuggester,
} from '../../../../components'
import { vMaska } from 'maska'
import { cloneDeep } from 'lodash'
import { useAuthPermissions } from '../../../../mixins'

// vue stuff
interface Props {
  addressSuggesterService: AddressSuggesterService
  municipalEntitiesService: MunicipalEntitiesService
  authService: AppAuthService
  citizen?: Citizen
  disabled?: boolean
  loading?: boolean
  documents?: CitizenDocumentOpts[]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CitizenForm',
  props: {
    addressSuggesterService: {},
    municipalEntitiesService: {},
    authService: {},
    citizen: {},
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    documents: {}
  },
  emits: ['onSubmit', 'cancel', 'delete'],
  setup(__props: any, { emit }) {

const props = __props;




const { currentMunicipalEntityId, hasPermissions } = useAuthPermissions(props.authService)
const hasManage = computed(() => {
  return hasPermissions(Permissions.CITIZENS_MANAGE)
})
const hasMeManage = computed(() => {
  return hasPermissions(Permissions.ME_CITIZENS_MANAGE)
})

// consts defining
const emptyDoc = {
  type: DocumentType.PASSPORT,
  num: undefined,
  series: undefined,
  issue: undefined,
  date: undefined,
  name: undefined,
  info: undefined,
}
const citizenOpts = useWatcherSavedRef<CitizenOpts>('CitizenForm', {
  documents: props.documents ? cloneDeep(props.documents) : [{ ...emptyDoc }],
  ...props.citizen,
  municipalEntityId:
    hasMeManage.value && !hasManage.value
      ? props.citizen?.municipalEntityId ?? currentMunicipalEntityId.value
      : props.citizen?.municipalEntityId,
})
const deleteConfirmation = ref(false)

const showDatepicker = ref(false)
const sexItems = Object.values(CitizenSex).map(s => {
  return {
    title: citizenSexName(s),
    value: s,
  }
})

const categoryItems = enumGetValues(CitizenCategory).map(c => {
  return {
    title: citizenCategoryName(c as CitizenCategory),
    value: c,
  }
})

const documentItems = computed(() => {
  return Object.values(DocumentType).map(d => {
    return {
      title: documentTypeName(d) || '-',
      value: d,
      disabled:
        d === DocumentType.PASSPORT && citizenOpts.value.documents.find(doc => doc.type === DocumentType.PASSPORT),
    }
  })
})

// validation
const requiredIfDoc = (docType: DocumentType) =>
  helpers.withParams({ docType: docType }, (value: unknown, docParams: CitizenDocumentOpts) => {
    if (value) return true
    return docParams.type !== docType
  })

const rules = {
  surname: {
    required: helpers.withMessage('Фамилия не может быть пустой', required),
  },
  name: {
    required: helpers.withMessage('Имя не может быть пустым', required),
  },
  sex: {
    required: helpers.withMessage('Пол не может быть пустым', required),
  },
  birthdate: {
    required: helpers.withMessage('Дата рождения не может быть пустой', required),
  },
  locality: {
    required: helpers.withMessage('Населённый пункт не может быть пустым', required),
  },
  street: {
    required: helpers.withMessage('Улица не может быть пустой', required),
  },
  house: {
    required: helpers.withMessage('Дом не может быть пустым', required),
  },
  category: {
    required: helpers.withMessage('Категория не может быть пустой', required),
  },
  municipalEntityId: {
    required: helpers.withMessage('Муниципальное образование не может быть пустым', required),
  },
  documents: {
    $each: helpers.forEach({
      series: {
        required: helpers.withMessage('Серия паспорта не может быть пустой', requiredIfDoc(DocumentType.PASSPORT)),
      },
      num: {
        required: helpers.withMessage('Номер паспорта не может быть пустым', requiredIfDoc(DocumentType.PASSPORT)),
      },
      date: {
        required: helpers.withMessage('Дата выдачи не может быть пустой', requiredIfDoc(DocumentType.PASSPORT)),
      },
      issue: {
        required: helpers.withMessage('Информация о выдаче может быть пустой', requiredIfDoc(DocumentType.PASSPORT)),
      },
      name: {
        required: helpers.withMessage('Название документа не может быть пустым', requiredIfDoc(DocumentType.OTHER)),
      },
      info: {
        required: helpers.withMessage('Информация о документе не может быть пустой', requiredIfDoc(DocumentType.OTHER)),
      },
    }),
  },
}

const validator = useVuelidate<CitizenOpts>(rules, citizenOpts)

const seriesMask = { mask: '####' }
const numMask = { mask: '######' }

// utility
const submit = async () => {
  const valid = await validator.value.$validate()

  if (valid) {
    emit('onSubmit', citizenOpts.value)
  }
}

const calcCategory = () => {
  if (!citizenOpts.value.category && citizenOpts.value.sex !== undefined && citizenOpts.value.birthdate) {
    citizenOpts.value.category = calcCitizenCategory(citizenOpts.value.sex, citizenOpts.value.birthdate)
  }
}

const getErrorMessages = (errors: ErrorObject[]) => {
  if (!validator.value.$dirty) {
    return undefined
  }
  return errors?.map((e: ErrorObject) => e.$message).join()
}

const addDocument = () => {
  const havePassport = citizenOpts.value.documents.find(d => d.type === DocumentType.PASSPORT)
  const docType = havePassport ? DocumentType.OTHER : DocumentType.PASSPORT

  citizenOpts.value.documents.push({ ...emptyDoc, type: docType })
}

const deleteDocument = (document: CitizenDocumentOpts) => {
  if (Object.entries(document).filter(e => e[0] !== 'type' && e[1]).length > 0) {
    // если что-нибудь уже заполнили, то запрашиваем подтверждение
    if (!confirm('Вы уверены, что хотите удалить документ?')) return
  }
  const docId = citizenOpts.value.documents.indexOf(document)
  citizenOpts.value.documents.splice(docId, 1)
}

const updateAddressWithSuggestion = (suggest: AddressSuggestion) => {
  if (suggest.municipalEntityId && suggest.municipalEntityId !== citizenOpts.value.municipalEntityId) {
    citizenOpts.value.municipalEntityId = suggest.municipalEntityId
  }
  if (suggest.address[AddressLevel.LOCALITY] && suggest.address[AddressLevel.LOCALITY] !== citizenOpts.value.locality) {
    citizenOpts.value.locality = suggest.address[AddressLevel.LOCALITY]
  }
  if (suggest.address[AddressLevel.STREET] && suggest.address[AddressLevel.STREET] !== citizenOpts.value.street) {
    citizenOpts.value.street = suggest.address[AddressLevel.STREET]
  }
  if (suggest.address[AddressLevel.HOUSE] && suggest.address[AddressLevel.HOUSE] !== citizenOpts.value.house) {
    citizenOpts.value.house = suggest.address[AddressLevel.HOUSE]
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_form = _resolveComponent("v-form")!
  const _directive_click_outside = _resolveDirective("click-outside")!

  return (_openBlock(), _createBlock(_component_v_form, {
    onSubmit: _withModifiers(submit, ["prevent"]),
    disabled: _ctx.loading || _ctx.disabled
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_row, { class: "mb-3" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(citizenOpts).surname,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(citizenOpts).surname) = $event)),
                label: "Фамилия*",
                variant: "outlined",
                "error-messages": getErrorMessages(_unref(validator).surname.$errors)
              }, null, 8, ["modelValue", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(citizenOpts).name,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(citizenOpts).name) = $event)),
                label: "Имя*",
                variant: "outlined",
                "error-messages": getErrorMessages(_unref(validator).name.$errors)
              }, null, 8, ["modelValue", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(citizenOpts).patronymic,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(citizenOpts).patronymic) = $event)),
                label: "Отчество",
                variant: "outlined"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_select, {
                modelValue: _unref(citizenOpts).sex,
                "onUpdate:modelValue": [
                  _cache[3] || (_cache[3] = ($event: any) => ((_unref(citizenOpts).sex) = $event)),
                  calcCategory
                ],
                items: _unref(sexItems),
                label: "Пол*",
                "no-data-text": "Нет данных",
                variant: "outlined",
                clearable: "",
                "error-messages": getErrorMessages(_unref(validator).sex.$errors)
              }, null, 8, ["modelValue", "items", "error-messages"])
            ]),
            _: 1
          }),
          _withDirectives((_openBlock(), _createBlock(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(VDateFieldV2), {
                modelValue: _unref(citizenOpts).birthdate,
                "onUpdate:modelValue": [
                  _cache[4] || (_cache[4] = ($event: any) => ((_unref(citizenOpts).birthdate) = $event)),
                  calcCategory
                ],
                label: "Дата рождения*",
                "error-messages": getErrorMessages(_unref(validator).birthdate.$errors)
              }, null, 8, ["modelValue", "error-messages"])
            ]),
            _: 1
          })), [
            [_directive_click_outside, () => (showDatepicker.value = false)]
          ]),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(VPhoneField), {
                label: "Телефон",
                phone: _unref(citizenOpts).phone,
                "onUpdate:phone": _cache[5] || (_cache[5] = ($event: any) => ((_unref(citizenOpts).phone) = $event))
              }, null, 8, ["phone"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(VMunicipalEntitiesSelect), {
                modelValue: _unref(citizenOpts).municipalEntityId,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(citizenOpts).municipalEntityId) = $event)),
                "municipal-entities-service": _ctx.municipalEntitiesService,
                "error-messages": getErrorMessages(_unref(validator).municipalEntityId.$errors),
                readonly: !hasManage.value && hasMeManage.value,
                disabled: _ctx.loading || _ctx.disabled,
                label: "Муниципальное образование*"
              }, null, 8, ["modelValue", "municipal-entities-service", "error-messages", "readonly", "disabled"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(AddressSuggester), {
                modelValue: _unref(citizenOpts).locality,
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((_unref(citizenOpts).locality) = $event)),
                "address-suggester-service": _ctx.addressSuggesterService,
                "municipal-entity-id": _unref(citizenOpts).municipalEntityId,
                level: _unref(AddressLevel).LOCALITY,
                "error-messages": getErrorMessages(_unref(validator).locality.$errors),
                label: "Населенный пункт*",
                "onUpdate:selectedValue": updateAddressWithSuggestion
              }, null, 8, ["modelValue", "address-suggester-service", "municipal-entity-id", "level", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(AddressSuggester), {
                modelValue: _unref(citizenOpts).street,
                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((_unref(citizenOpts).street) = $event)),
                "address-suggester-service": _ctx.addressSuggesterService,
                "municipal-entity-id": _unref(citizenOpts).municipalEntityId,
                "other-fields": { [_unref(AddressLevel).LOCALITY]: _unref(citizenOpts).locality },
                level: _unref(AddressLevel).STREET,
                "error-messages": getErrorMessages(_unref(validator).street.$errors),
                label: "Улица*",
                "onUpdate:selectedValue": updateAddressWithSuggestion
              }, null, 8, ["modelValue", "address-suggester-service", "municipal-entity-id", "other-fields", "level", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(AddressSuggester), {
                modelValue: _unref(citizenOpts).house,
                "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((_unref(citizenOpts).house) = $event)),
                "address-suggester-service": _ctx.addressSuggesterService,
                "municipal-entity-id": _unref(citizenOpts).municipalEntityId,
                "other-fields": {
            [_unref(AddressLevel).LOCALITY]: _unref(citizenOpts).locality,
            [_unref(AddressLevel).STREET]: _unref(citizenOpts).street,
          },
                level: _unref(AddressLevel).HOUSE,
                "error-messages": getErrorMessages(_unref(validator).house.$errors),
                label: "Дом*",
                "onUpdate:selectedValue": updateAddressWithSuggestion
              }, null, 8, ["modelValue", "address-suggester-service", "municipal-entity-id", "other-fields", "level", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(citizenOpts).appartment,
                "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((_unref(citizenOpts).appartment) = $event)),
                label: "Квартира",
                variant: "outlined"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_select, {
                modelValue: _unref(citizenOpts).category,
                "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((_unref(citizenOpts).category) = $event)),
                items: _unref(categoryItems),
                label: "Категория*",
                "no-data-text": "Нет данных",
                variant: "outlined",
                clearable: "",
                "error-messages": getErrorMessages(_unref(validator).category.$errors)
              }, null, 8, ["modelValue", "items", "error-messages"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(citizenOpts).documents, (document, index) => {
        return (_openBlock(), _createBlock(_component_v_row, {
          key: index,
          class: "mt-3 mb-3"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_col, {
              cols: "12",
              md: "12"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_1, [
                  _createElementVNode("p", _hoisted_2, "Документ " + _toDisplayString(_unref(citizenOpts).documents.length > 1 ? index + 1 : ''), 1),
                  _withDirectives(_createVNode(_component_v_btn, {
                    onClick: ($event: any) => (deleteDocument(document)),
                    disabled: _ctx.disabled || _ctx.loading,
                    variant: 'outlined',
                    icon: "mdi-delete"
                  }, null, 8, ["onClick", "disabled"]), [
                    [_vShow, _unref(citizenOpts).documents.length > 1]
                  ])
                ])
              ]),
              _: 2
            }, 1024),
            _createVNode(_component_v_col, {
              cols: "12",
              md: "6"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_select, {
                  modelValue: document.type,
                  "onUpdate:modelValue": [($event: any) => ((document.type) = $event), 
            type => {
              Object.assign(document, {
                ...emptyDoc,
                type,
              })
            }
          ],
                  items: documentItems.value,
                  label: "Тип",
                  variant: "outlined"
                }, {
                  item: _withCtx(({ props, item }) => [
                    _createVNode(_component_v_list_item, _mergeProps(props, {
                      disabled: !!item.raw.disabled
                    }), null, 16, ["disabled"])
                  ]),
                  _: 2
                }, 1032, ["modelValue", "onUpdate:modelValue", "items"])
              ]),
              _: 2
            }, 1024),
            (document.type === _unref(DocumentType).PASSPORT)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createVNode(_component_v_col, {
                    cols: "12",
                    md: "6"
                  }, {
                    default: _withCtx(() => [
                      _withDirectives(_createVNode(_component_v_text_field, {
                        modelValue: document.series,
                        "onUpdate:modelValue": ($event: any) => ((document.series) = $event),
                        label: "Серия паспорта*",
                        variant: "outlined",
                        "error-messages": getErrorMessages(_unref(validator).documents.$each.$response.$errors[index].series)
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "error-messages"]), [
                        [_unref(vMaska), void 0, seriesMask]
                      ])
                    ]),
                    _: 2
                  }, 1024),
                  _createVNode(_component_v_col, {
                    cols: "12",
                    md: "6"
                  }, {
                    default: _withCtx(() => [
                      _withDirectives(_createVNode(_component_v_text_field, {
                        modelValue: document.num,
                        "onUpdate:modelValue": ($event: any) => ((document.num) = $event),
                        label: "Номер паспорта*",
                        variant: "outlined",
                        "error-messages": getErrorMessages(_unref(validator).documents.$each.$response.$errors[index].num)
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "error-messages"]), [
                        [_unref(vMaska), void 0, numMask]
                      ])
                    ]),
                    _: 2
                  }, 1024),
                  _createVNode(_component_v_col, {
                    cols: "12",
                    md: "6"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_text_field, {
                        modelValue: document.issue,
                        "onUpdate:modelValue": ($event: any) => ((document.issue) = $event),
                        label: "Кем выдан*",
                        variant: "outlined",
                        "error-messages": getErrorMessages(_unref(validator).documents.$each.$response.$errors[index].issue)
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "error-messages"])
                    ]),
                    _: 2
                  }, 1024),
                  _createVNode(_component_v_col, {
                    cols: "12",
                    md: "6"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(VDateFieldV2), {
                        modelValue: document.date,
                        "onUpdate:modelValue": ($event: any) => ((document.date) = $event),
                        label: "Дата выдачи*",
                        "error-messages": getErrorMessages(_unref(validator).documents.$each.$response.$errors[index].date),
                        "start-time": {
              hours: 0,
              minutes: 0,
              seconds: 0,
            }
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "error-messages"])
                    ]),
                    _: 2
                  }, 1024)
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createVNode(_component_v_col, {
                    cols: "12",
                    md: "6"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_text_field, {
                        modelValue: document.name,
                        "onUpdate:modelValue": ($event: any) => ((document.name) = $event),
                        label: "Название документа*",
                        variant: "outlined",
                        "error-messages": getErrorMessages(_unref(validator).documents.$each.$response.$errors[index].name)
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "error-messages"])
                    ]),
                    _: 2
                  }, 1024),
                  _createVNode(_component_v_col, {
                    cols: "12",
                    md: "6"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_text_field, {
                        modelValue: document.info,
                        "onUpdate:modelValue": ($event: any) => ((document.info) = $event),
                        label: "Данные документа*",
                        variant: "outlined",
                        "error-messages": getErrorMessages(_unref(validator).documents.$each.$response.$errors[index].info)
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "error-messages"])
                    ]),
                    _: 2
                  }, 1024)
                ]))
          ]),
          _: 2
        }, 1024))
      }), 128)),
      _createVNode(_component_v_row, { justify: "space-between" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "auto" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                disabled: _ctx.disabled || _ctx.loading,
                variant: "tonal",
                onClick: addDocument
              }, {
                default: _withCtx(() => [
                  _createTextVNode("Добавить ещё документ")
                ]),
                _: 1
              }, 8, ["disabled"])
            ]),
            _: 1
          }),
          (_ctx.citizen && (hasManage.value || hasMeManage.value))
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_col, { cols: "auto" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_btn, {
                      loading: _ctx.loading,
                      disabled: _ctx.disabled,
                      color: "red-accent-4",
                      variant: "outlined",
                      onClick: _cache[12] || (_cache[12] = ($event: any) => (deleteConfirmation.value = true))
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode("Удалить")
                      ]),
                      _: 1
                    }, 8, ["loading", "disabled"])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DeleteConfirmationDialog), {
                  modelValue: deleteConfirmation.value,
                  "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((deleteConfirmation).value = $event)),
                  onAgree: _cache[14] || (_cache[14] = 
            () => {
              emit('delete', _ctx.citizen?.id)
              deleteConfirmation.value = false
            }
          )
                }, {
                  title: _withCtx(() => [
                    _createTextVNode(" Подтвердите действие ")
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ], 64))
            : _createCommentVNode("", true),
          _createVNode(_component_v_col, { cols: "auto" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                type: "submit",
                color: "primary",
                loading: _ctx.loading,
                disabled: _ctx.disabled
              }, {
                default: _withCtx(() => [
                  _createTextVNode(" Сохранить ")
                ]),
                _: 1
              }, 8, ["loading", "disabled"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["onSubmit", "disabled"]))
}
}

})